
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { BiPhoneIncoming, BiInfoCircle } from "react-icons/bi";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { io } from "socket.io-client";

const InboundCRM = ({ user }) => {
    const [inboundNumber, setInboundNumber] = useState(process.env.REACT_APP_TWILIO_PHONE_NUMBER || "+1234567890");
    const [isActiveCall, setIsActiveCall] = useState(false);
    const [callDetails, setCallDetails] = useState(null);

    useEffect(() => {
        const socket = io(process.env.REACT_APP_SERVER_API);

        socket.on("inboundCallInitiated", (data) => {
            console.log("Inbound call received:", data);
            setIsActiveCall(true);
            setCallDetails(data);
        });

        socket.on("callEnded", () => {
            setIsActiveCall(false);
            setCallDetails(null);
        });

        return () => socket.disconnect();
    }, []);

    return (
        <motion.div
            className="bg-gray-50 p-8 min-h-screen"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
        >
            <div className="max-w-4xl mx-auto">
                <div className="bg-white p-6 shadow-lg rounded-xl mb-8">
                    <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
                        <BiPhoneIncoming className="text-blue-600" /> Inbound Call Testing
                    </h1>
                    <p className="text-gray-600 mt-2">
                        Monitor and test your inbound call flows in real-time.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Status Card */}
                    <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100 flex flex-col items-center justify-center text-center">
                        {isActiveCall ? (
                            <div className="space-y-4">
                                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto animate-pulse">
                                    <BiPhoneIncoming className="text-green-600 text-4xl" />
                                </div>
                                <h3 className="text-2xl font-bold text-gray-800">Active Inbound Call</h3>
                                <p className="text-gray-600">From: {callDetails?.from || "Unknown"}</p>
                                <button
                                    className="bg-blue-600 text-white px-6 py-2 rounded-full hover:bg-blue-700 transition"
                                    onClick={() => window.open(`/call/${encodeURIComponent("Inbound Customer")}/${encodeURIComponent(callDetails?.from || "Unknown")}`, "_blank")}
                                >
                                    Join Conversation
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto">
                                    <AiOutlineLoading3Quarters className="text-blue-600 text-4xl animate-spin" />
                                </div>
                                <h3 className="text-xl font-semibold text-gray-800">Waiting for Inbound Call...</h3>
                                <p className="text-gray-500 font-mono bg-gray-100 p-2 rounded">
                                    Your Twilio Number: {inboundNumber}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Instructions Card */}
                    <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100">
                        <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                            <BiInfoCircle className="text-blue-500" /> How to Test
                        </h3>
                        <ul className="space-y-4 text-gray-600">
                            <li className="flex gap-3">
                                <span className="bg-blue-100 text-blue-600 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">1</span>
                                <span>Dial the Twilio number displayed on the left from your phone.</span>
                            </li>
                            <li className="flex gap-3">
                                <span className="bg-blue-100 text-blue-600 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">2</span>
                                <span>The system will automatically recognize the incoming call and trigger the AI agent.</span>
                            </li>
                            <li className="flex gap-3">
                                <span className="bg-blue-100 text-blue-600 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">3</span>
                                <span>Wait for this page to update or check the "Inbound Dashboard" for session metrics.</span>
                            </li>
                            <li className="flex gap-3">
                                <span className="bg-blue-100 text-blue-600 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">4</span>
                                <span>You can also view the live transcription by clicking "Join Conversation" when the call is active.</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default InboundCRM;
